// src/components/UploadButton.js

import React, { useRef, useState, useContext, forwardRef, useImperativeHandle } from 'react';
import { Dropdown, DropdownButton, Modal } from 'react-bootstrap';
import { LanguageContext } from '../contexts/LanguageContext';
import { uploadImage } from '../services/apiService';
import ImageCropper from './ImageCropper';
import LoadingSpinner from './LoadingSpinner'; // <-- Make sure this import is present!
import useAlertDialog from '../hooks/useAlertDialog';
import AlertDialog from './AlertDialog';

// This function downscales an image file to a maximum dimension (default: 1024px)
function downscaleImage(file, maxDimension = 1024) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        // If the image is already small enough, resolve with the original file
        if (width <= maxDimension && height <= maxDimension) {
          resolve(file);
        } else {
          let newWidth, newHeight;
          if (width > height) {
            newWidth = maxDimension;
            newHeight = (height / width) * maxDimension;
          } else {
            newHeight = maxDimension;
            newWidth = (width / height) * maxDimension;
          }
          // Create an offscreen canvas and draw the downscaled image into it
          const canvas = document.createElement('canvas');
          canvas.width = newWidth;
          canvas.height = newHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          // Convert the canvas back to a Blob (and then to a File)
          canvas.toBlob((blob) => {
            if (blob) {
              const newFile = new File([blob], file.name, { type: file.type });
              resolve(newFile);
            } else {
              reject(new Error('Canvas is empty'));
            }
          }, file.type, 0.9);
        }
      };
      img.onerror = (err) => reject(err);
      img.src = event.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

const UploadButton = forwardRef(({ onUpload, openScanner, showUI }, ref) => {
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModal, setIsModal] = useState(false); 
  const [loading, setLoading] = useState(false); // <-- The missing loading state
  const { translations } = useContext(LanguageContext);
  const { dialogState: alertState, showAlert, hideAlert } = useAlertDialog();

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    triggerFileInput: (fromBottomNav = false) => {
      setIsModal(fromBottomNav);
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    },
    setImage: (imageFile) => {
      setSelectedImage(imageFile);
      setIsModal(true); // Open in modal if triggered externally
    },
  }));

  // Handle direct "Upload" from the dropdown
  const handleUploadClick = () => {
    setIsModal(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle "Scan Barcode" from the dropdown
  const handleScanClick = () => {
    openScanner();
  };

  // When user picks a file, downscale it if needed before setting it for cropping
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      downscaleImage(file)
        .then((resizedFile) => {
          setSelectedImage(resizedFile);
        })
        .catch((error) => {
          console.error("Downscale error:", error);
          // Fallback to the original file if downscaling fails
          setSelectedImage(file);
        });
    }
  };

  /**
   * Upload the cropped image and inform parent component
   */
  const handleSearch = async (croppedImageBlob) => {
    setLoading(true); // Start loading
    const formData = new FormData();
    formData.append('image', croppedImageBlob, 'cropped_image.jpg');

    try {
      const response = await uploadImage(formData);

      // Condition: only alert in dev, not in production
      if (process.env.NODE_ENV !== 'production') {
        await showAlert({
          title: translations.success || 'Success',
          message: response.data.message || 'Image processed successfully.',
          variant: 'success',
          autoClose: true,
          autoCloseDelay: 2000,
        });
      }

      if (response.data.products) {
        onUpload(response.data.products);
      } else {
        onUpload([]);
      }
      // ---- NEW CODE START: Save search history with uploaded image ----
      const newHistoryItem = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        products: response.data.products,
        uploadedImage: null,
      };
      const reader = new FileReader();
      reader.onloadend = () => {
        newHistoryItem.uploadedImage = reader.result;
        try {
          const stored = JSON.parse(localStorage.getItem('searchHistory') || '[]');
          stored.push(newHistoryItem);
          localStorage.setItem('searchHistory', JSON.stringify(stored));
        } catch (err) {
          if (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
            try {
              let stored = JSON.parse(localStorage.getItem('searchHistory') || '[]');
              // Remove oldest entries until there is enough room
              while (stored.length > 0) {
                stored.shift();
                try {
                  localStorage.setItem('searchHistory', JSON.stringify(stored));
                  // Try saving the new entry again
                  stored.push(newHistoryItem);
                  localStorage.setItem('searchHistory', JSON.stringify(stored));
                  break;
                } catch (e) {
                  if (stored.length === 0) {
                    console.error("LocalStorage quota exceeded even after clearing history.");
                    break;
                  }
                }
              }
            } catch (e) {
              console.error("Error handling quota exceeded:", e);
            }
          } else {
            console.error("Error saving search history to localStorage:", err);
          }
        }
      };
      reader.readAsDataURL(croppedImageBlob);
      // ---- NEW CODE END ----
      setSelectedImage(null); // Close the cropper after upload
    } catch (error) {
      console.error('Error uploading cropped image:', error);
      await showAlert({
        title: translations.error || 'Error',
        message: translations.errorUploadingImage || 'Error uploading image.',
        variant: 'danger',
      });
    } finally {
      setLoading(false); // Done
    }
  };

  // Dismiss cropping
  const handleCancel = () => {
    setSelectedImage(null);
  };

  return (
    <div className="text-center">
      {/* Alert Dialog */}
      <AlertDialog
        show={alertState.show}
        onHide={hideAlert}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        icon={alertState.icon}
        autoClose={alertState.autoClose}
        autoCloseDelay={alertState.autoCloseDelay}
        showCloseButton={alertState.showCloseButton}
      />
      
      {/* Show a spinner if loading is true */}
      {loading && <LoadingSpinner />}

      {/* If no image selected, show the initial UI (Dropdown or nothing) */}
      {!selectedImage && showUI && !loading && (
        <DropdownButton
          id="upload-scan-dropdown"
          title={translations.uploadProductImage}
          variant="primary"
          size="lg"
        >
          <Dropdown.Item onClick={handleUploadClick}>
            {translations.uploadProductImage}
          </Dropdown.Item>
          <Dropdown.Item onClick={handleScanClick}>
            {translations.scanBarcode}
          </Dropdown.Item>
        </DropdownButton>
      )}

      {/* Inline Cropper (for top nav usage) */}
      {selectedImage && !isModal && !loading && (
        <ImageCropper
          imageFile={selectedImage}
          onSearch={handleSearch}
          onCancel={handleCancel}
        />
      )}

      {/* Modal Cropper (for bottom nav usage) */}
      {selectedImage && isModal && !loading && (
        <Modal
          show={selectedImage !== null}
          onHide={handleCancel}
          size="lg"
          centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>{translations.uploadProductImage}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ImageCropper
              imageFile={selectedImage}
              onSearch={handleSearch}
              onCancel={handleCancel}
            />
          </Modal.Body>
          {/* No separate footer, so we don’t duplicate Cancel buttons */}
        </Modal>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept="image/*"
      />
    </div>
  );
});

export default UploadButton;
